import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { format, addDays } from "date-fns";
import { tr } from "date-fns/locale";
import {
  ArrowLeft,
  MoreVertical,
  User,
  Banknote,
  Receipt,
  CalendarClock,
  Clock,
  Image,
  Trash,
  MoreHorizontal,
  QrCode,
  Loader2,
  Handshake,
  NotebookPen
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { people, loadPeople, loadPersonTransactions, deleteTransaction } from "@/data/data";
import Footer from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import BottomNavigation from "@/components/BottomNavigation";
import PersonHeader from "@/components/person-detail/PersonHeader";
import { Person, Transaction } from "@/types";
import ImageViewDialog from "@/components/ImageViewDialog";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import CheckStatusDialog from "@/components/CheckStatusDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { entriesAPI } from "@/api/entries";
import { peopleAPI } from "@/api/people";
import OpenAccountSettleDialog from "@/components/OpenAccountSettleDialog";
import type { AdvanceSubMethod } from "@/lib/paymentMethods";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { getCurrencySymbol } from "@/utils/currencyUtils";
import { CapacitorBarcodeScanner } from '@capacitor/barcode-scanner';
import { formatInDeviceTimezone, adjustDateFromDB } from "@/utils/dateUtils";
import { ScreenHeader, MonoValue, StatusBadge } from "@/components/redesign";

// Style for hiding background during QR scan
const scannerStyles = `
.scanner-active {
  visibility: hidden !important;
  opacity: 0 !important;
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: -1 !important;
}

.scanner-container {
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 9999 !important;
  background: transparent !important;
}
`;

const PersonDetailPage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { t } = useTranslation();

  // Get the preferred currency and its symbol
  const preferredCurrency = preferences?.base_currency || "TRY";
  const currencySymbol = getCurrencySymbol(preferredCurrency);
  const showOfficialUnofficial = preferences?.base_official_divide || false;

  // State
  const [person, setPerson] = useState<Person | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [calculatedTotalAgreed, setCalculatedTotalAgreed] = useState(0);
  const [calculatedTotalPaid, setCalculatedTotalPaid] = useState(0);
  const [calculatedRemainingBalance, setCalculatedRemainingBalance] = useState(0);
  const [viewingImage, setViewingImage] = useState<{ url: string; title: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [checkStatusDialogOpen, setCheckStatusDialogOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<Transaction | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  // Payment-method ledgers (combined across all projects) in the preferred currency
  const [advanceCredit, setAdvanceCredit] = useState(0);
  const [openAccountPayable, setOpenAccountPayable] = useState(0);
  // Açık hesap settlement dialog target
  const [settleTarget, setSettleTarget] = useState<{ id: string; outstanding: number; currency: string } | null>(null);

  // Load person data
  useEffect(() => {
    const fetchData = async () => {
      if (!personId) return;

      setLoading(true);
      try {
        // Load person data
        const peopleData = await loadPeople();
        const personData = peopleData.find(p => p.id === personId) || null;
        setPerson(personData);

        // Load transactions
        const transactionsData = await loadPersonTransactions(personId);
        setTransactions(transactionsData);

        const transactionsWithImages = transactionsData.filter(t => t.image_url);

      } catch (error) {

        toast({
          title: t("common.error"),
          description: t("person_detail.loading_person"),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Add event listener to refresh data when triggered from other components
    const handlePersonDataRefresh = (event: any) => {
      if (event.detail.personId === personId) {
        fetchData();
      }
    };

    window.addEventListener('person-data-refresh', handlePersonDataRefresh);

    return () => {
      window.removeEventListener('person-data-refresh', handlePersonDataRefresh);
    };
  }, [personId, toast]);

  // Inject scanner styles
  useEffect(() => {
    // Add scanner styles to head
    const styleElement = document.createElement('style');
    styleElement.innerHTML = scannerStyles;
    document.head.appendChild(styleElement);

    return () => {
      // Clean up styles when component unmounts
      document.head.removeChild(styleElement);

      // Make sure to stop scanning if component unmounts while scanning
      if (isScanning) {
        document.querySelector('body')?.classList.remove('scanner-active');
      }
    };
  }, [isScanning]);

  // Filter transactions by type
  const laborCosts = transactions.filter(t => t.transactionType === 'laborCost');
  // Real payments exclude açık hesap debt rows (unpaid liabilities) and advance
  // draw-downs (counts_toward_cost === false), which are reconciliations, not cash out.
  const payments = transactions.filter(t =>
    t.transactionType === 'payment' &&
    t.payment_method !== 'open_account' &&
    t.counts_toward_cost !== false
  );
  const checks = transactions.filter(t => t.transactionType === 'check');
  const openAccounts = transactions.filter(t => t.payment_method === 'open_account');

  // Sum of settlement payments per açık hesap (original currency)
  const settlementsByOpenAccount = transactions.reduce((map, t) => {
    if (t.settles_entry_id) {
      map[t.settles_entry_id] = (map[t.settles_entry_id] || 0) + (Number(t.original_amount) || 0);
    }
    return map;
  }, {} as Record<string, number>);
  const outstandingFor = (oa: Transaction) =>
    Math.max(0, (Number(oa.original_amount) || 0) - (settlementsByOpenAccount[oa.id] || 0));

  // Calculate totals based on the user's preferred currency
  const getAmountInPreferredCurrency = (transaction: Transaction) => {
    const currencyKey = preferredCurrency.toLowerCase();

    // Create a currency map as suggested in the guidelines
    const currencyMap = {
      'try': {
        official: transaction.amount_try_official || 0,
        unofficial: transaction.amount_try_unofficial || 0,
        fallback: transaction.amount // Legacy fallback
      },
      'usd': {
        official: transaction.amount_usd_official || 0,
        unofficial: transaction.amount_usd_unofficial || 0,
        fallback: transaction.usdAmount // Legacy fallback
      },
      'eur': {
        official: transaction.amount_eur_official || 0,
        unofficial: transaction.amount_eur_unofficial || 0,
        fallback: transaction.euroAmount // Legacy fallback
      },
      'gbp': {
        official: transaction.amount_gbp_official || 0,
        unofficial: transaction.amount_gbp_unofficial || 0
      },
      'jpy': {
        official: transaction.amount_jpy_official || 0,
        unofficial: transaction.amount_jpy_unofficial || 0
      },
      'rub': {
        official: transaction.amount_rub_official || 0,
        unofficial: transaction.amount_rub_unofficial || 0
      },
      'kwd': {
        official: transaction.amount_kwd_official || 0,
        unofficial: transaction.amount_kwd_unofficial || 0
      },
      'sar': {
        official: transaction.amount_sar_official || 0,
        unofficial: transaction.amount_sar_unofficial || 0
      },
      'chf': {
        official: transaction.amount_chf_official || 0,
        unofficial: transaction.amount_chf_unofficial || 0
      },
      'xau': {
        official: transaction.amount_xau_official || 0,
        unofficial: transaction.amount_xau_unofficial || 0
      },
      'btc': {
        official: transaction.amount_btc_official || 0,
        unofficial: transaction.amount_btc_unofficial || 0
      }
    };

    // Get currency data for the preferred currency
    const currencyData = currencyMap[currencyKey];
    if (!currencyData) return transaction.amount; // Fallback to original amount

    // Choose official or unofficial based on entry status and user preferences
    if (showOfficialUnofficial) {
      if (transaction.is_official && currencyData.official > 0) {
        return currencyData.official;
      } else if (!transaction.is_official && currencyData.unofficial > 0) {
        return currencyData.unofficial;
      }
    }

    // If not splitting official/unofficial or specific type not available
    return currencyData.official || currencyData.unofficial || currencyData.fallback || transaction.amount;
  };

  // Format currency value based on currency type
  const formatCurrencyValue = (value: number): string => {
    // Set decimal places based on currency
    let decimalPlaces = 2; // Default for most currencies
    if (preferredCurrency === 'XAU') decimalPlaces = 4;
    if (preferredCurrency === 'BTC') decimalPlaces = 6;

    return value.toLocaleString('tr-TR', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
  };

  // Calculate totals with the preferred currency AFTER transactions are loaded
  useEffect(() => {
    let agreed = 0;
    let paid = 0;

    transactions.forEach(t => {
      const amount = getAmountInPreferredCurrency(t);
      if (t.transactionType === 'laborCost') {
        agreed += amount;
      } else if (
        t.transactionType === 'payment' &&
        t.payment_method !== 'open_account' &&
        t.counts_toward_cost !== false
      ) {
        paid += amount;
      } else if (t.transactionType === 'check' && t.checkType === 'givenCheck' && t.status === 'alindi') {
        paid += amount;
      } else if (t.transactionType === 'check' && t.checkType === 'receivedCheck' && t.status === 'alindi') {
        paid += amount;
      }
    });

    setCalculatedTotalAgreed(agreed);
    setCalculatedTotalPaid(paid);
    setCalculatedRemainingBalance(agreed - paid);

  }, [transactions, preferredCurrency, showOfficialUnofficial]);

  // Load advance (prepaid credit) and open-account (payable) ledgers for this person
  useEffect(() => {
    if (!personId) return;
    let active = true;
    (async () => {
      try {
        const [adv, open] = await Promise.all([
          entriesAPI.getPersonAdvanceBalance(personId),
          entriesAPI.getPersonOpenAccountBalance(personId),
        ]);
        if (!active) return;
        setAdvanceCredit(adv?.[preferredCurrency] ?? 0);
        setOpenAccountPayable(open?.[preferredCurrency] ?? 0);
      } catch {
        // ignore — ledgers are supplementary
      }
    })();
    return () => {
      active = false;
    };
  }, [personId, preferredCurrency, transactions]);

  // Function to handle opening the image dialog
  const handleViewImage = (url: string, title: string) => {
    setViewingImage({ url, title });
  };

  // Function to handle delete transaction
  const handleDeleteClick = (transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  // Function to confirm deletion
  const confirmDelete = async () => {
    if (!transactionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTransaction(transactionToDelete.id);

      // Update local state
      setTransactions(prev => prev.filter(t => t.id !== transactionToDelete.id));

      // Show success toast
      toast({
        title: t("person_detail.transaction_deleted"),
        description: t("person_detail.transaction_deleted_desc"),
      });
    } catch (error) {

      toast({
        title: t("common.error"),
        description: t("person_detail.delete_error"),
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  // Function to handle check status
  const handleCheckStatusClick = (check: Transaction) => {
    setSelectedCheck(check);
    setCheckStatusDialogOpen(true);
  };

  // Function to update check status
  const handleCheckStatusUpdate = async (status: 'alindi' | 'ertelendi' | 'alinamadi', newDueDate?: Date) => {
    if (!selectedCheck || !user?.id) return;

    try {
      // Update check status
      await entriesAPI.updateCheckStatus(
        selectedCheck.id,
        status,
        newDueDate,
        user.id
      );

      // Show success toast based on status
      let message = "";
      if (status === 'alindi') {
        message = selectedCheck.checkType === 'givenCheck'
          ? t("person_detail.check_paid")
          : t("person_detail.check_collected");
      } else if (status === 'ertelendi') {
        message = t("person_detail.check_postponed");
      } else {
        message = selectedCheck.checkType === 'givenCheck'
          ? t("person_detail.check_not_paid")
          : t("person_detail.check_not_collected");
      }

      toast({
        title: t("person_detail.check_status_updated"),
        description: message,
      });

      // Reload transactions to reflect changes
      if (personId) {
        const updatedTransactions = await loadPersonTransactions(personId);
        setTransactions(updatedTransactions);
      }
    } catch (error) {

      toast({
        title: t("common.error"),
        description: t("person_detail.check_update_error"),
        variant: "destructive"
      });
    } finally {
      setCheckStatusDialogOpen(false);
      setSelectedCheck(null);
    }
  };

  // Function to handle entry edit
  const handleEditEntry = (transaction: Transaction) => {
    navigate(`/entry/${transaction.id}`);
  };

  // Function to handle QR scan
  const handleQRScan = async () => {
    try {
      // Hide the background and set scanning state
      setIsScanning(true);
      document.querySelector('body')?.classList.add('scanner-active');

      // Start the scan with proper options
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: 17, // ALL barcode types
        scanInstructions: t("person_detail.scan_instructions")
      });

      // Scanning completed
      setIsScanning(false);
      document.querySelector('body')?.classList.remove('scanner-active');

      // Extract content from the result (based on ScanResult property)
      const scannedUserId = result.ScanResult;

      if (scannedUserId && scannedUserId.trim() !== '') {

        // Only proceed if we have a valid person ID and scanned user ID
        if (!personId) {
          toast({
            title: t("common.error"),
            description: t("person_detail.connection_info_missing"),
            variant: "destructive"
          });
          return;
        }

        try {
          // Use our API function to handle the linking process
          const success = await peopleAPI.linkPersonWithQR(personId, scannedUserId);

          if (success) {
            toast({
              title: t("person_detail.connection_successful"),
              description: t("person_detail.connection_successful_desc"),
            });

            // Navigate to the new person ID (the scanned one)
            navigate(`/person/${scannedUserId}`, { replace: true });
          }
        } catch (error: any) {

          toast({
            title: t("common.error"),
            description: error.message || t("person_detail.connection_error"),
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: t("common.error"),
          description: t("person_detail.qr_scan_failed"),
          variant: "destructive"
        });
      }
    } catch (error) {

      toast({
        title: t("common.error"),
        description: t("person_detail.qr_scan_cancelled"),
        variant: "destructive"
      });
    } finally {
      // Always ensure we stop scanning and restore UI
      setIsScanning(false);
      document.querySelector('body')?.classList.remove('scanner-active');
    }
  };

  // Function to handle unlinking
  const handleUnlink = async () => {
    if (!person || !personId) return;

    try {
      // Use the peopleAPI.unlinkPerson function to handle the unlinking process
      const success = await peopleAPI.unlinkPerson(personId);

      if (success) {
        // Show success message
        toast({
          title: t("common.success"),
          description: t("person_detail.unlink_successful"),
        });

        // Navigate back to people list, as the current URL will be invalid
        navigate('/people');
      }
    } catch (error: any) {

      toast({
        title: t("common.error"),
        description: error.message || t("person_detail.unlink_error"),
        variant: "destructive",
      });
    }
  };

  // Render transaction card — light theme
  // Record a (partial or full) payment against an açık hesap, then refresh
  const handleOpenAccountPayment = async (amount: number, method: AdvanceSubMethod, dueDate?: Date) => {
    if (!settleTarget) return;
    try {
      await entriesAPI.addOpenAccountPayment(settleTarget.id, amount, method, dueDate, user?.id);
      toast({
        title: "Başarılı",
        description: method === "check" ? "Çek ile ödeme kaydedildi." : "Ödeme kaydedildi.",
      });
      if (personId) {
        const updated = await loadPersonTransactions(personId);
        setTransactions(updated);
      }
      setSettleTarget(null);
    } catch (e: any) {
      toast({
        title: t("common.error"),
        description: e?.message || "İşlem başarısız oldu.",
        variant: "destructive",
      });
      throw e;
    }
  };

  const renderTransactionCard = (transaction: Transaction, type: 'laborCost' | 'payment' | 'check') => {
    let borderColor: string;
    let iconColor: string;
    let checkDueToday = false;

    if (type === 'laborCost') {
      borderColor = "border-brand-200";
      iconColor = "text-brand-600";
    } else if (type === 'payment') {
      borderColor = "border-success-200";
      iconColor = "text-success-600";
    } else {
      borderColor = "border-warning-300";
      iconColor = "text-warning-600";
    }

    if (type === 'check' && transaction.dueInDays !== undefined) {
      const today = new Date();
      const dueDate = new Date(new Date(transaction.date).getTime() + (transaction.dueInDays * 24 * 60 * 60 * 1000));
      checkDueToday = today.toDateString() === dueDate.toDateString();
    }

    // Get amount in preferred currency
    const displayAmount = getAmountInPreferredCurrency(transaction);

    return (
      <div className="relative" key={transaction.id}>
        <Card
          className={`bg-white border ${borderColor} shadow-sm rounded-2xl p-4 cursor-pointer hover:shadow-md transition-shadow ${checkDueToday ? "ring-2 ring-error-600" : ""}`}
          onClick={() => handleEditEntry(transaction)}
        >
          {type === 'laborCost' && (
            <>
              <div className="mb-1 flex flex-col gap-2 pr-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 truncate">{transaction.description}</h4>
                  <p className="text-[12px] text-gray-500 mt-0.5">{transaction.projectName || transaction.projectId}</p>
                </div>
                <MonoValue
                  value={displayAmount}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="md"
                  tone="brand"
                  className="flex-shrink-0 ml-2"
                />
              </div>
              <div className="flex flex-col gap-2 text-[12px] text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <span>{transaction.metricDetails}</span>
                <div className="flex gap-2 items-center">
                  {preferences?.second_currency && transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.second_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                  {preferences?.third_currency && transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.third_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                  <MonoValue value={format(new Date(transaction.date), "d MMM yyyy", { locale: tr })} size="meta" tone="muted" />
                </div>
              </div>
              {transaction.image_url && (
                <div className="flex justify-end mt-2 pt-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-brand-600 border-brand-200 hover:bg-brand-50 py-0 h-7 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewImage(transaction.image_url!, transaction.description);
                    }}
                  >
                    <Image className="h-3.5 w-3.5 mr-1.5" /> {t("person_detail.view_image")}
                  </Button>
                </div>
              )}
            </>
          )}

          {type === 'payment' && (
            <>
              <div className="mb-1 flex flex-col gap-2 pr-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 truncate">{transaction.description}</h4>
                  <p className="text-[12px] text-gray-500 mt-0.5">{transaction.projectName || transaction.projectId}</p>
                </div>
                <MonoValue
                  value={displayAmount}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="md"
                  tone="success"
                  className="flex-shrink-0 ml-2"
                />
              </div>
              <div className="flex flex-col gap-2 text-[12px] text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <span>{transaction.paymentType || t("person_detail.payments")}</span>
                <div className="flex gap-2 items-center">
                  {preferences?.second_currency && transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.second_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                  {preferences?.third_currency && transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.third_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                  <MonoValue value={format(new Date(transaction.date), "d MMM yyyy", { locale: tr })} size="meta" tone="muted" />
                </div>
              </div>
              {transaction.image_url && (
                <div className="flex justify-end mt-2 pt-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-success-600 border-success-200 hover:bg-success-50 py-0 h-7 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewImage(transaction.image_url!, transaction.description);
                    }}
                  >
                    <Image className="h-3.5 w-3.5 mr-1.5" /> {t("person_detail.view_image")}
                  </Button>
                </div>
              )}
            </>
          )}

          {type === 'check' && (
            <>
              <div className="mb-1 flex flex-col gap-2 pr-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-gray-900 truncate">
                    {transaction.checkType === 'givenCheck' ? t("projects.given_check") : t("projects.received_check")}
                    {transaction.checkNumber ? ` #${transaction.checkNumber}` : ''}
                  </h4>
                  <p className="text-[12px] text-gray-500 mt-0.5">{transaction.projectName || transaction.projectId}</p>
                </div>
                <MonoValue
                  value={displayAmount}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="md"
                  tone="default"
                  className="flex-shrink-0 ml-2"
                />
              </div>

              <div className="flex flex-col gap-2 text-[12px] text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <MonoValue
                  value={transaction.dueInDays !== undefined
                    ? `${t("entries.due_date")}: ${formatInDeviceTimezone(
                        addDays(adjustDateFromDB(transaction.date), transaction.dueInDays),
                        "d MMM yyyy",
                        { locale: tr }
                      )}`
                    : formatInDeviceTimezone(adjustDateFromDB(transaction.date), "d MMM yyyy", { locale: tr })}
                  size="meta"
                  tone="muted"
                />
                <div className="flex gap-2 items-center">
                  {preferences?.second_currency && transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.second_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.second_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                  {preferences?.third_currency && transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`] && (
                    <MonoValue
                      value={transaction[`amount_${preferences.third_currency.toLowerCase()}_${transaction.is_official ? 'official' : 'unofficial'}`]}
                      format
                      fractionDigits={2}
                      prefix={`${getCurrencySymbol(preferences.third_currency)} `}
                      size="meta"
                      tone="muted"
                    />
                  )}
                </div>
              </div>

              {transaction.image_url && (
                <div className="flex justify-end mt-2 pt-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-warning-600 border-warning-300 hover:bg-warning-50 py-0 h-7 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewImage(transaction.image_url!, transaction.description);
                    }}
                  >
                    <Image className="h-3.5 w-3.5 mr-1.5" /> {t("person_detail.view_image")}
                  </Button>
                </div>
              )}

              {checkDueToday && (
                <div className="mt-2 pt-2 border-t border-gray-200 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-error-600 border-error-100 hover:bg-error-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckStatusClick(transaction);
                    }}
                  >
                    {t("projects.due_today")} - {t("projects.update_status")}
                  </Button>
                </div>
              )}
            </>
          )}

          <button
            className="absolute top-3 right-3 text-gray-400 hover:text-error-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(transaction);
            }}
            aria-label={t("common.delete")}
          >
            <Trash className="h-4 w-4" />
          </button>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container bg-[var(--gray-25,#FDFDFD)]">
        <ScreenHeader title={t("common.loading")} />
        <main className="main-content">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-brand-600" />
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="page-container bg-[var(--gray-25,#FDFDFD)]">
        <ScreenHeader title={t("people_ui.user_not_found")} />
        <main className="main-content">
          <div className="px-4 pb-4 flex items-center justify-center h-full">
            <div className="text-center">
              <p className="mb-4 text-gray-600">{t("people_ui.user_not_found")}</p>
              <Button onClick={() => navigate(-1)} variant="outline" className="border-gray-300">
                {t("common.back")}
              </Button>
            </div>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="page-container bg-[var(--gray-25,#FDFDFD)]">
      <ScreenHeader title={person.name} />

      <main className="main-content">
        <div className="px-4 pb-4">
          {/* Person header with avatar, name, and contact details */}
          <PersonHeader person={person} />

          {/* QR Linking Button */}
          <div className="my-4 flex justify-center">
            {person && person.linked ? (
              <Button
                onClick={handleUnlink}
                variant="outline"
                className="w-full border-error-100 text-error-600 hover:bg-error-50 rounded-xl"
              >
                <QrCode className="h-4 w-4 mr-2" /> {t("people.remove_connection")}
              </Button>
            ) : (
              <Button
                onClick={handleQRScan}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-xl"
              >
                <QrCode className="h-4 w-4 mr-2" /> {t("people.connect_with_qr")}
              </Button>
            )}
          </div>

          {/* Financial Overview — navy summary cards */}
          <section className="rounded-2xl bg-brand-900 px-4 py-4 mb-4 shadow-sm">
            <h3 className="text-[13px] font-semibold text-white/70 mb-3 uppercase tracking-[0.04em]">
              {t("people.financial_overview")}
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5">
                <p className="text-[11px] text-white/60 mb-1 flex items-center gap-1">
                  <Banknote className="h-3 w-3" /> {t("person_detail.total_agreed")}
                </p>
                <MonoValue
                  value={calculatedTotalAgreed}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="sm"
                  tone="white"
                />
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5">
                <p className="text-[11px] text-white/60 mb-1 flex items-center gap-1">
                  <Receipt className="h-3 w-3" /> {t("person_detail.total_paid")}
                </p>
                <MonoValue
                  value={calculatedTotalPaid}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="sm"
                  tone="white"
                />
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5">
                <p className="text-[11px] text-white/60 mb-1 flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" /> {t("person_detail.remaining_balance")}
                </p>
                <MonoValue
                  value={calculatedRemainingBalance}
                  format
                  fractionDigits={2}
                  prefix={currencySymbol}
                  size="sm"
                  tone="white"
                />
              </div>
            </div>
          </section>

          {/* Payment-method ledgers: prepaid advance & open-account payable */}
          {(advanceCredit > 0.005 || openAccountPayable > 0.005) && (
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              {advanceCredit > 0.005 && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-[11px] text-emerald-700/80 mb-1 flex items-center gap-1">
                    <Handshake className="h-3 w-3" /> Avans (Kalan)
                  </p>
                  <MonoValue
                    value={advanceCredit}
                    format
                    fractionDigits={2}
                    prefix={currencySymbol}
                    size="sm"
                  />
                  <p className="text-[11px] text-emerald-700/70 mt-1">
                    Bu kişiye peşin ödenen, henüz faturalanmamış tutar.
                  </p>
                </div>
              )}
              {openAccountPayable > 0.005 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-[11px] text-amber-700/80 mb-1 flex items-center gap-1">
                    <NotebookPen className="h-3 w-3" /> Açık Hesap (Borç)
                  </p>
                  <MonoValue
                    value={openAccountPayable}
                    format
                    fractionDigits={2}
                    prefix={currencySymbol}
                    size="sm"
                  />
                  <p className="text-[11px] text-amber-700/70 mt-1">
                    Bu kişiye ödenecek (henüz maliyete eklenmemiş) tutar.
                  </p>
                </div>
              )}
            </section>
          )}

          {/* Transactions Sections */}
          <div className="space-y-6">
            {/* Agreed Labor Costs */}
            <div className="space-y-3">
              <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 px-1">
                <div className="h-4 w-1 bg-brand-600 rounded" />
                {t("person_detail.labor_costs")}
              </h3>
              {laborCosts.length > 0 ? (
                <div className="space-y-2">
                  {laborCosts.map(cost => renderTransactionCard(cost, 'laborCost'))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">{t("people.no_labor_cost")}</div>
              )}
            </div>

            {/* Payments Made */}
            <div className="space-y-3">
              <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 px-1">
                <div className="h-4 w-1 bg-success-600 rounded" />
                {t("person_detail.payments")}
              </h3>
              {payments.length > 0 ? (
                <div className="space-y-2">
                  {payments.map(payment => renderTransactionCard(payment, 'payment'))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">{t("people.no_payments")}</div>
              )}
            </div>

            {/* Açık Hesap (Open Accounts) */}
            {openAccounts.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 px-1">
                  <div className="h-4 w-1 bg-amber-500 rounded" />
                  Açık Hesap
                </h3>
                <div className="space-y-2">
                  {openAccounts.map(oa => {
                    const total = getAmountInPreferredCurrency(oa);
                    const remaining = outstandingFor(oa);
                    const isPaid = remaining <= 0.005;
                    const oaSymbol = getCurrencySymbol(oa.original_currency || "TRY");
                    return (
                      <Card key={oa.id} className="bg-white border border-amber-200 shadow-sm rounded-2xl p-4">
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-gray-900 truncate">{oa.description || "Açık hesap"}</h4>
                            <p className="text-[12px] text-gray-500 mt-0.5">{oa.projectName || oa.projectId}</p>
                          </div>
                          <MonoValue
                            value={total}
                            format
                            fractionDigits={2}
                            prefix={currencySymbol}
                            size="md"
                            tone="brand"
                            className="flex-shrink-0 ml-2"
                          />
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className={`text-[12px] font-medium ${isPaid ? "text-success-600" : "text-amber-600"}`}>
                            {isPaid
                              ? "Ödendi"
                              : `Kalan: ${oaSymbol}${remaining.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`}
                          </span>
                          {!isPaid && (
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              onClick={() =>
                                setSettleTarget({ id: oa.id, outstanding: remaining, currency: oa.original_currency || "TRY" })
                              }
                            >
                              Öde
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checks Given */}
            <div className="space-y-3">
              <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2 px-1">
                <div className="h-4 w-1 bg-warning-600 rounded" />
                {t("person_detail.checks")}
              </h3>
              {checks.length > 0 ? (
                <div className="space-y-2">
                  {checks.map(check => renderTransactionCard(check, 'check'))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">{t("people.no_checks")}</div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />

      {/* Image Viewing Dialog */}
      {viewingImage && (
        <ImageViewDialog
          imageUrl={viewingImage.url}
          title={viewingImage.title}
          open={!!viewingImage}
          onOpenChange={(open) => {
            if (!open) setViewingImage(null);
          }}
        />
      )}

      {/* Açık Hesap settlement dialog */}
      <OpenAccountSettleDialog
        open={!!settleTarget}
        onOpenChange={(o) => { if (!o) setSettleTarget(null); }}
        outstanding={settleTarget?.outstanding || 0}
        currencySymbol={getCurrencySymbol(settleTarget?.currency || "TRY")}
        onPay={handleOpenAccountPayment}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={t("common.delete")}
        description={t("person_detail.delete_confirmation")}
      />

      {/* Check Status Dialog */}
      {selectedCheck && (
        <CheckStatusDialog
          open={checkStatusDialogOpen}
          onOpenChange={setCheckStatusDialogOpen}
          isGivenCheck={selectedCheck.checkType === 'givenCheck'}
          checkId={selectedCheck.id}
          checkAmount={selectedCheck.amount}
          checkDescription={selectedCheck.description}
          onStatusUpdate={handleCheckStatusUpdate}
        />
      )}
    </div>
  );
};

export default PersonDetailPage;
